import { useState } from 'react'
import { Play, X } from 'lucide-react'
import type { Embed, EmbedMedia } from '@/types/api'
import { cx } from '@/lib/cx'
import { renderMarkdown, type RenderOptions } from '@/lib/markdown'
import { formatDateTime } from '@/lib/format'
import styles from './EmbedCard.module.css'

/** Домены плееров, которые можно открыть в iframe: только известные видеохостинги (спецификация 2.1). */
const PLAYER_HOSTS = new Set(['www.youtube-nocookie.com', 'www.youtube.com', 'rutube.ru', 'vk.com', 'vkvideo.ru'])

const MAX_W = 400
const MAX_H = 300

interface Props {
  embed: Embed
  /** Скрыть превью: только автор и модератор. */
  onRemove?: (() => void) | undefined
  /** Упоминания и разметка в описании rich-карточек (оповещения автомода, боты). */
  render?: RenderOptions | undefined
}

/** Превью ссылки: карточка с цветной полосой, картинка или плеер видеохостинга. */
export function EmbedCard({ embed, onRemove, render }: Props) {
  const [playing, setPlaying] = useState(false)
  const color = embed.color != null ? `#${embed.color.toString(16).padStart(6, '0')}` : undefined
  const player = playerUrl(embed.video)
  const remove = onRemove ? (
    <button type="button" className={styles.remove} data-tooltip="Скрыть превью" aria-label="Скрыть превью" onClick={onRemove}><X size={16} /></button>
  ) : null

  if (embed.type === 'image' && embed.thumbnail) {
    const size = fit(embed.thumbnail)
    return (
      <div className={styles.imageOnly}>
        <a href={embed.url} target="_blank" rel="noreferrer noopener" className={styles.imageLink}>
          <img className={styles.image} src={src(embed.thumbnail)} alt="" loading="lazy" style={size} />
        </a>
        {remove}
      </div>
    )
  }

  const thumb = embed.thumbnail && !player ? embed.thumbnail : null
  return (
    <div className={styles.card} style={color ? { borderLeftColor: color } : undefined}>
      <div className={styles.body}>
        <div className={styles.text}>
          {embed.provider?.name ? <span className={styles.provider}>{embed.provider.name}</span> : null}
          {embed.author?.name ? (
            embed.author.url ? <a className={styles.author} href={embed.author.url} target="_blank" rel="noreferrer noopener">{embed.author.name}</a> : <span className={styles.author}>{embed.author.name}</span>
          ) : null}
          {embed.title ? <a className={styles.title} href={embed.url} target="_blank" rel="noreferrer noopener">{embed.title}</a> : null}
          {embed.description ? (
            embed.type === 'rich' ? <div className={cx(styles.description, styles.rich)}>{renderMarkdown(embed.description, render ?? {})}</div> : <p className={styles.description}>{embed.description}</p>
          ) : null}
          {embed.fields?.length ? (
            <div className={styles.fields}>
              {embed.fields.map((f, i) => (
                <div key={i} className={cx(styles.field, f.inline && styles.fieldInline)}>
                  <span className={styles.fieldName}>{f.name}</span>
                  <div className={styles.fieldValue}>{renderMarkdown(f.value, render ?? {})}</div>
                </div>
              ))}
            </div>
          ) : null}
          {embed.footer?.text || embed.timestamp ? (
            <span className={styles.footer}>
              {embed.footer?.text}
              {embed.footer?.text && embed.timestamp ? ' · ' : ''}
              {embed.timestamp ? formatDateTime(new Date(embed.timestamp)) : ''}
            </span>
          ) : null}
        </div>
        {thumb ? (
          <a className={styles.thumb} href={embed.url} target="_blank" rel="noreferrer noopener">
            <img src={src(thumb)} alt="" loading="lazy" />
          </a>
        ) : null}
      </div>
      {player ? (
        playing ? (
          <iframe
            className={styles.player}
            src={player}
            title={embed.title ?? 'Видео'}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-forms"
            referrerPolicy="no-referrer"
          />
        ) : (
          <button type="button" className={styles.poster} onClick={() => setPlaying(true)} aria-label="Воспроизвести видео">
            {embed.thumbnail ? <img src={src(embed.thumbnail)} alt="" loading="lazy" /> : null}
            <span className={styles.play}><Play size={28} fill="currentColor" /></span>
          </button>
        )
      ) : null}
      {embed.image && !player ? (
        <a href={embed.url} target="_blank" rel="noreferrer noopener" className={cx(styles.imageLink, styles.bigImage)}>
          <img className={styles.image} src={src(embed.image)} alt="" loading="lazy" style={fit(embed.image)} />
        </a>
      ) : null}
      {remove}
    </div>
  )
}

const src = (m: EmbedMedia) => m.proxy_url ?? m.url

/** Размер картинки в пределах 400×300 с сохранением пропорций, чтобы список не прыгал при загрузке. */
function fit(m: EmbedMedia): { width: number; aspectRatio: string } | undefined {
  if (!m.width || !m.height) return undefined
  const k = Math.min(1, MAX_W / m.width, MAX_H / m.height)
  return { width: Math.round(m.width * k), aspectRatio: `${m.width} / ${m.height}` }
}

function playerUrl(v: EmbedMedia | undefined): string | null {
  if (!v?.url) return null
  try {
    const u = new URL(v.url)
    if (u.protocol !== 'https:' || !PLAYER_HOSTS.has(u.hostname)) return null
    u.searchParams.set('autoplay', '1')
    return u.toString()
  } catch {
    return null
  }
}
